import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import {
  getConceptsForShot,
  createConcept,
  selectConcept,
  deleteConcept,
  generateConceptImage,
  getConceptVersions,
  getConceptImageUrl,
} from "@/services/shotService";
import { useCollaboration } from "@/contexts/CollaborationContext";
import { useAuth } from "@/contexts/AuthContext";
// ShotRedesignPanel replaced by inline EditDrawerContent for horizontal layout
import {
  X, Plus, Loader2, ChevronLeft, Save, Clock, Check, Crown,
  Edit3, Trash2, User,
} from "lucide-react";

// ─── Version History Dialog ─────────────────────────────────────────────────
function VersionDialog({ projectId, concept, onClose }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getConceptVersions(projectId, concept.concept_id)
      .then((v) => { if (!cancelled) setVersions(v || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, concept.concept_id]);

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-[var(--bg-surface)] border border-[var(--border-default)] rounded-2xl shadow-2xl w-full max-w-lg max-h-[70vh] flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-[var(--border-subtle)] flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
              <Clock size={14} className="text-[var(--color-gold-400)]" />
              Version History
            </h3>
            <p className="text-[10px] text-[var(--text-muted)] mt-0.5">{concept.name} — {concept.creator_name}</p>
          </div>
          <button onClick={onClose} className="btn-ghost btn-icon-xs"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-[var(--text-disabled)]" /></div>
          ) : versions.length === 0 ? (
            <div className="text-center py-8">
              <Clock size={24} className="mx-auto text-[var(--text-disabled)] mb-2" />
              <p className="text-sm text-[var(--text-muted)]">No versions saved yet</p>
              <p className="text-[10px] text-[var(--text-disabled)] mt-1">Versions are created when you regenerate or edit</p>
            </div>
          ) : (
            <div className="space-y-3">
              {versions.map((ver) => (
                <div key={ver.version_id} className="flex items-start gap-3 p-3 rounded-xl border border-[var(--border-subtle)] hover:border-[var(--border-default)] transition-colors">
                  {ver.image_data ? (
                    <div className="w-24 aspect-video rounded-lg overflow-hidden flex-shrink-0 bg-black">
                      <img src={`data:image/png;base64,${ver.image_data}`} alt="" className="w-full h-full object-cover" />
                    </div>
                  ) : <div className="w-24 aspect-video rounded-lg bg-[var(--bg-overlay)] flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-xs font-medium text-[var(--text-primary)] truncate">{ver.name}</p>
                      <span className="text-[9px] text-[var(--color-gold-400)] font-mono flex-shrink-0">v{ver.version_number}</span>
                    </div>
                    <p className="text-[10px] text-[var(--text-muted)]">{ver.created_by_name || "Unknown"} — {new Date(ver.created_at).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Inline concept image cell ──────────────────────────────────────────────
function ConceptCell({ concept, isSelected, onSelect, onEdit, onVersions, onDelete, isOwner, keyframeLabel }) {
  const [imgError, setImgError] = useState(false);
  const hasImage = concept.status === "completed" && !imgError;

  return (
    <div className={`rounded-xl overflow-hidden border-2 transition-all duration-200 ${
      isSelected
        ? "border-[var(--color-gold-500)] shadow-md shadow-[rgba(212,168,83,0.15)]"
        : "border-[var(--border-subtle)] hover:border-[var(--border-default)]"
    } bg-[var(--bg-surface)]`}>
      {/* Image */}
      <div className="aspect-video bg-[var(--bg-muted)] relative">
        {hasImage ? (
          <img
            src={getConceptImageUrl(concept.concept_id)}
            alt={concept.name}
            className="w-full h-full object-cover"
            loading="lazy"

            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {concept.status === "generating" ? (
              <Loader2 className="w-6 h-6 text-[var(--color-gold-400)] animate-spin" />
            ) : (
              <Edit3 className="w-5 h-5 text-[var(--text-disabled)]" />
            )}
          </div>
        )}
        {isSelected && (
          <div className="absolute top-2 left-2 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[var(--color-gold-500)] text-black text-[8px] font-bold">
            <Crown size={10} /> Selected
          </div>
        )}
        {keyframeLabel && (
          <div className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-[8px] text-white/70">{keyframeLabel}</div>
        )}
      </div>
      {/* Compact actions */}
      <div className="flex items-center gap-1 p-1.5">
        {!isSelected && (
          <button onClick={() => onSelect?.(concept)}
            className="flex-1 flex items-center justify-center gap-1 py-1 rounded text-[10px] bg-[rgba(212,168,83,0.1)] text-[var(--color-gold-500)] hover:bg-[rgba(212,168,83,0.2)] transition-colors">
            <Check size={11} /> Select
          </button>
        )}
        <button onClick={() => onEdit?.(concept)}
          className="p-1 rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-muted)] transition-colors">
          <Edit3 size={12} />
        </button>
        {concept.version_count > 0 && (
          <button onClick={() => onVersions?.(concept)}
            className="p-1 rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-muted)] transition-colors text-[9px] font-mono">
            v{concept.version_count}
          </button>
        )}
        {isOwner && (
          <button onClick={() => onDelete?.(concept)}
            className="p-1 rounded text-[var(--text-disabled)] hover:text-[var(--color-error)] transition-colors">
            <Trash2 size={11} />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Horizontal Edit Drawer ─────────────────────────────────────────────────
function EditDrawerContent({ concept, onRegenerate, onUpdate }) {
  const [prompt, setPrompt] = useState(concept?.current_prompt || "");
  const [shotType, setShotType] = useState(concept?.settings?.shot_type || "medium");
  const [cameraAngle, setCameraAngle] = useState(concept?.settings?.camera_angle || "eye-level");
  const [cameraMovement, setCameraMovement] = useState(concept?.settings?.camera_movement || "static");
  const [lighting, setLighting] = useState(concept?.settings?.lighting || "natural");
  const [lens, setLens] = useState(concept?.settings?.lens || "50mm");
  const [regenerating, setRegenerating] = useState(false);
  const [refImage, setRefImage] = useState(null);
  const fileRef = useRef(null);

  const handleRegen = async () => {
    if (!prompt.trim()) return;
    setRegenerating(true);
    try {
      await onRegenerate(concept.concept_id, prompt, {
        shot_type: shotType, camera_angle: cameraAngle,
        camera_movement: cameraMovement, lighting, lens,
        reference_image: refImage || undefined,
      });
    } finally { setRegenerating(false); }
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setRefImage(reader.result.split(",")[1]);
    reader.readAsDataURL(file);
  };

  const selectCls = "w-full py-1 px-2 rounded-lg bg-[var(--bg-overlay)] border border-[var(--border-subtle)] text-[10px] text-[var(--text-primary)] focus:outline-none focus:border-[rgba(212,168,83,0.5)]";

  return (
    <div className="flex gap-6 px-6 py-3 h-[calc(100%-36px)] overflow-x-auto">
      {/* Column 1: Prompt + Regenerate */}
      <div className="flex-1 min-w-[280px] flex flex-col gap-2">
        <label className="text-[9px] text-[var(--text-disabled)] uppercase tracking-wider">Prompt</label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          className="flex-1 resize-none rounded-lg bg-[var(--bg-overlay)] border border-[var(--border-subtle)] text-xs text-[var(--text-primary)] p-3 focus:outline-none focus:border-[rgba(212,168,83,0.5)]"
          placeholder="Describe the shot..."
        />
        <button onClick={handleRegen} disabled={regenerating || !prompt.trim()}
          className="btn-primary w-full py-2 text-xs flex items-center justify-center gap-2">
          {regenerating ? <Loader2 size={14} className="animate-spin" /> : <Edit3 size={14} />}
          Regenerate
        </button>
      </div>

      {/* Column 2: Equipment */}
      <div className="w-48 flex flex-col gap-2 flex-shrink-0">
        <label className="text-[9px] text-[var(--text-disabled)] uppercase tracking-wider">Equipment</label>
        <div className="space-y-1.5 flex-1">
          <select value={shotType} onChange={(e) => setShotType(e.target.value)} className={selectCls}>
            {["wide","medium","close-up","extreme-close-up","establishing","over-shoulder","pov","two-shot","insert","aerial"].map(v =>
              <option key={v} value={v}>{v.replace(/-/g," ")}</option>)}
          </select>
          <select value={cameraAngle} onChange={(e) => setCameraAngle(e.target.value)} className={selectCls}>
            {["eye-level","low-angle","high-angle","dutch","birds-eye","worms-eye"].map(v =>
              <option key={v} value={v}>{v.replace(/-/g," ")}</option>)}
          </select>
          <select value={cameraMovement} onChange={(e) => setCameraMovement(e.target.value)} className={selectCls}>
            {["static","pan","tilt","dolly","tracking","crane","handheld","steadicam","zoom"].map(v =>
              <option key={v} value={v}>{v}</option>)}
          </select>
          <select value={lighting} onChange={(e) => setLighting(e.target.value)} className={selectCls}>
            {["natural","dramatic","low-key","high-key","silhouette","golden-hour","neon","moonlight"].map(v =>
              <option key={v} value={v}>{v.replace(/-/g," ")}</option>)}
          </select>
          <select value={lens} onChange={(e) => setLens(e.target.value)} className={selectCls}>
            {["14mm","24mm","35mm","50mm","85mm","100mm","135mm","200mm"].map(v =>
              <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
      </div>

      {/* Column 3: Reference image */}
      <div className="w-44 flex flex-col gap-2 flex-shrink-0">
        <label className="text-[9px] text-[var(--text-disabled)] uppercase tracking-wider">Reference</label>
        <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
        {refImage ? (
          <div className="relative flex-1">
            <img src={`data:image/png;base64,${refImage}`} alt="Ref" className="w-full h-full object-cover rounded-lg border border-[var(--border-subtle)]" />
            <button onClick={() => setRefImage(null)} className="absolute top-1 right-1 p-0.5 bg-black/60 rounded-full"><X size={10} className="text-white" /></button>
          </div>
        ) : (
          <button onClick={() => fileRef.current?.click()}
            className="flex-1 border-2 border-dashed border-[var(--border-subtle)] rounded-lg flex flex-col items-center justify-center gap-1 hover:border-[rgba(212,168,83,0.4)] transition-colors">
            <Plus size={16} className="text-[var(--text-disabled)]" />
            <span className="text-[9px] text-[var(--text-muted)]">Upload ref</span>
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Main ConceptBoard ──────────────────────────────────────────────────────
export default function ConceptBoard({ projectId, anchor, onClose, onUpdate }) {
  const { user } = useAuth();
  const { onEvent, emitConceptCreated, emitConceptSelected, emitConceptUpdated } = useCollaboration();

  // anchor.frames is an array of frame objects (1 for single shot, N for multi-frame)
  const shotFrames = anchor?.frames || [];
  const isMultiFrame = shotFrames.length > 1;

  // conceptsByAnchor: { anchorId: [concepts] }
  const [conceptsByAnchor, setConceptsByAnchor] = useState({});
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingConcept, setEditingConcept] = useState(null);
  const [versionConcept, setVersionConcept] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const anchorIds = useMemo(() =>
    [...new Set(shotFrames.map(f => f.anchor_id).filter(Boolean))],
    [shotFrames]
  );

  const fetchAllConcepts = useCallback(async () => {
    if (!projectId || anchorIds.length === 0) { setLoading(false); return; }
    try {
      const results = {};
      await Promise.all(anchorIds.map(async (aid) => {
        const data = await getConceptsForShot(projectId, aid);
        results[aid] = data || [];
      }));
      setConceptsByAnchor(results);
    } catch (err) {
      console.error("Failed to fetch concepts:", err);
    } finally {
      setLoading(false);
    }
  }, [projectId, anchorIds]);

  useEffect(() => { fetchAllConcepts(); }, [fetchAllConcepts]);

  // Real-time
  useEffect(() => {
    if (!onEvent) return;
    const unsubs = [
      onEvent("new_concept", () => fetchAllConcepts()),
      onEvent("concept_selection_changed", (data) => {
        setConceptsByAnchor(prev => {
          const next = { ...prev };
          for (const aid of Object.keys(next)) {
            next[aid] = next[aid].map(c => ({ ...c, is_selected: c.concept_id === data.concept_id }));
          }
          return next;
        });
      }),
      onEvent("concept_image_updated", () => fetchAllConcepts()),
    ];
    return () => unsubs.forEach((u) => u?.());
  }, [onEvent, fetchAllConcepts]);

  // Get all unique creators across all anchors
  const creators = useMemo(() => {
    const seen = new Map();
    for (const concepts of Object.values(conceptsByAnchor)) {
      for (const c of concepts) {
        if (!seen.has(c.creator_id)) {
          seen.set(c.creator_id, { creator_id: c.creator_id, creator_name: c.creator_name, creator_picture: c.creator_picture });
        }
      }
    }
    return [...seen.values()];
  }, [conceptsByAnchor]);

  // For single-frame: flatten all concepts into one list
  const allConcepts = useMemo(() => {
    return Object.values(conceptsByAnchor).flat();
  }, [conceptsByAnchor]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      // Create concept for the first anchor (or all anchors for multi-frame)
      const firstAnchorId = anchorIds[0];
      const result = await createConcept({
        project_id: projectId,
        anchor_id: firstAnchorId,
        prompt: anchor?.selected_text || "",
      });
      emitConceptCreated?.(firstAnchorId, result);
      toast.success("Concept created — generating image...");
      await fetchAllConcepts();
      setEditingConcept(result);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create concept");
    } finally {
      setCreating(false);
    }
  };

  const handleSelect = async (concept) => {
    // Optimistic
    setConceptsByAnchor(prev => {
      const next = { ...prev };
      for (const aid of Object.keys(next)) {
        next[aid] = next[aid].map(c => ({
          ...c,
          is_selected: c.anchor_id === concept.anchor_id
            ? c.concept_id === concept.concept_id
            : c.is_selected
        }));
      }
      return next;
    });
    try {
      await selectConcept(concept.concept_id);
      emitConceptSelected?.(concept.anchor_id, concept.concept_id);
      onUpdate?.();
    } catch {
      fetchAllConcepts();
      toast.error("Failed to select concept");
    }
  };

  const handleDelete = async (concept) => {
    try {
      await deleteConcept(concept.concept_id);
      setDeleteConfirm(null);
      if (editingConcept?.concept_id === concept.concept_id) setEditingConcept(null);
      setConceptsByAnchor(prev => {
        const next = { ...prev };
        for (const aid of Object.keys(next)) {
          next[aid] = next[aid].filter(c => c.concept_id !== concept.concept_id);
        }
        return next;
      });
      onUpdate?.();
    } catch { toast.error("Failed to delete"); }
  };

  const handleRegenerate = async (conceptId, prompt, equipment = {}) => {
    try {
      await generateConceptImage(conceptId, { prompt, settings: equipment });
      const concept = allConcepts.find(c => c.concept_id === conceptId);
      if (concept) emitConceptUpdated?.(conceptId, concept.anchor_id);
      toast.success("Regenerated");
      await fetchAllConcepts();
      onUpdate?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to regenerate"); }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  const renderMultiFrameGrid = () => {
    // Columns = creators, Rows = keyframes
    // For each creator, find their concept per anchor
    return (
      <div className="overflow-x-auto overflow-y-auto flex-1">
        <table className="border-collapse">
          <thead>
            <tr>
              <th className="p-2 text-left text-[10px] text-[var(--text-disabled)] font-normal sticky left-0 bg-[rgba(9,9,11,0.95)] z-10 w-20">
                Frame
              </th>
              {creators.map((cr) => (
                <th key={cr.creator_id} className="p-2 text-center min-w-[220px]">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <div className="w-6 h-6 rounded-full bg-[var(--bg-overlay)] flex items-center justify-center overflow-hidden flex-shrink-0">
                      {cr.creator_picture ? (
                        <img src={cr.creator_picture} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User size={12} className="text-[var(--text-muted)]" />
                      )}
                    </div>
                    <span className="text-xs text-[var(--text-primary)] font-medium">{cr.creator_name}</span>
                  </div>
                </th>
              ))}
              {/* Add column */}
              <th className="p-2 min-w-[120px]" />
            </tr>
          </thead>
          <tbody>
            {shotFrames.map((frame, rowIdx) => (
              <tr key={frame.id}>
                {/* Row label */}
                <td className="p-2 text-[10px] text-[var(--text-muted)] sticky left-0 bg-[rgba(9,9,11,0.95)] z-10 align-top">
                  <div className="font-medium">{frame.keyframe_label || `Frame ${rowIdx + 1}`}</div>
                  <div className="text-[8px] text-[var(--text-disabled)] capitalize">{frame.shot_type}</div>
                </td>
                {/* Creator columns */}
                {creators.map((cr) => {
                  const concepts = conceptsByAnchor[frame.anchor_id] || [];
                  const concept = concepts.find(c => c.creator_id === cr.creator_id);
                  return (
                    <td key={cr.creator_id} className="p-1.5 align-top">
                      {concept ? (
                        <ConceptCell
                          concept={concept}
                          isSelected={concept.is_selected}
                          isOwner={concept.creator_id === user?.user_id}
                          keyframeLabel={null}
                          onSelect={handleSelect}
                          onEdit={(c) => setEditingConcept(c)}
                          onVersions={(c) => setVersionConcept(c)}
                          onDelete={(c) => setDeleteConfirm(c)}
                        />
                      ) : (
                        <div className="aspect-video rounded-xl border border-dashed border-[var(--border-subtle)] flex items-center justify-center">
                          <span className="text-[9px] text-[var(--text-disabled)]">—</span>
                        </div>
                      )}
                    </td>
                  );
                })}
                {/* Add column for first row only */}
                {rowIdx === 0 && (
                  <td rowSpan={shotFrames.length} className="p-2 align-middle">
                    <button onClick={handleCreate} disabled={creating}
                      className="w-28 h-20 rounded-xl border-2 border-dashed border-[var(--border-default)] hover:border-[rgba(212,168,83,0.5)] flex flex-col items-center justify-center gap-1 transition-colors">
                      {creating ? <Loader2 size={16} className="text-[var(--color-gold-400)] animate-spin" /> : (
                        <><Plus size={16} className="text-[var(--text-disabled)]" /><span className="text-[9px] text-[var(--text-muted)]">Add</span></>
                      )}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderSingleShotCards = () => {
    const concepts = allConcepts;
    return (
      <div className="flex-1 overflow-x-auto overflow-y-hidden">
        <div className="h-full p-6 flex items-center">
          {concepts.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center">
              <div className="w-14 h-14 rounded-2xl bg-[var(--bg-overlay)] flex items-center justify-center mb-4">
                <Plus className="w-7 h-7 text-[var(--text-disabled)]" />
              </div>
              <h3 className="text-base font-semibold text-[var(--text-primary)] mb-1">No Concepts Yet</h3>
              <p className="text-xs text-[var(--text-muted)] max-w-sm mb-5">
                Each team member can propose their vision. Add yours and the team picks the best one.
              </p>
              <button onClick={handleCreate} disabled={creating} className="btn-primary flex items-center gap-2 px-5 py-2.5">
                {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                Add First Concept
              </button>
            </div>
          ) : (
            <div className="flex gap-5 items-start">
              {concepts.map((concept) => (
                <ConceptCell
                  key={concept.concept_id}
                  concept={concept}
                  isSelected={concept.is_selected}
                  isOwner={concept.creator_id === user?.user_id}
                  onSelect={handleSelect}
                  onEdit={(c) => setEditingConcept(c)}
                  onVersions={(c) => setVersionConcept(c)}
                  onDelete={(c) => setDeleteConfirm(c)}
                />
              ))}
              <button onClick={handleCreate} disabled={creating}
                className="flex-shrink-0 w-56 aspect-video rounded-2xl border-2 border-dashed border-[var(--border-default)] hover:border-[rgba(212,168,83,0.5)] flex flex-col items-center justify-center gap-2 transition-colors">
                {creating ? <Loader2 size={16} className="text-[var(--color-gold-400)] animate-spin" /> : (
                  <><Plus size={16} className="text-[var(--text-disabled)]" /><span className="text-[10px] text-[var(--text-muted)]">Add Concept</span></>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-[rgba(9,9,11,0.95)] backdrop-blur-xl flex flex-col">
      {/* Header */}
      <div className="h-14 px-6 border-b border-[var(--border-subtle)] bg-[rgba(17,17,19,0.8)] backdrop-blur-sm flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="btn-ghost btn-icon-sm"><ChevronLeft size={20} /></button>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-[var(--text-primary)]">
                {isMultiFrame ? `Multi-Frame Shot (${shotFrames.length} keyframes)` : "Shot Concepts"}
              </h2>
              {anchor?.scene_number && <span className="chip chip-gold text-[9px]">Scene {anchor.scene_number}</span>}
              <span className="text-[10px] text-[var(--text-disabled)]">{creators.length} contributor{creators.length !== 1 ? "s" : ""}</span>
            </div>
            <p className="text-[10px] text-[var(--text-muted)] mt-0.5 max-w-sm truncate">{anchor?.selected_text || ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { onUpdate?.(); onClose(); }} className="btn-secondary flex items-center gap-2 px-4 py-2 text-sm">
            <Save size={14} /> Save
          </button>
          <button onClick={handleCreate} disabled={creating} className="btn-primary flex items-center gap-2 px-4 py-2 text-sm">
            {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Add My Concept
          </button>
          <button onClick={onClose} className="btn-ghost btn-icon-sm"><X size={18} /></button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-[var(--color-gold-400)] animate-spin" />
        </div>
      ) : isMultiFrame && creators.length > 0 ? (
        <div className="flex-1 flex flex-col overflow-hidden p-4">
          {renderMultiFrameGrid()}
        </div>
      ) : (
        renderSingleShotCards()
      )}

      {/* Bottom edit drawer — full-width horizontal layout */}
      <AnimatePresence>
        {editingConcept && (
          <motion.div
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 350 }}
            className="border-t border-[var(--border-subtle)] bg-[var(--bg-surface)] flex-shrink-0"
            style={{ height: "220px" }}
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between px-6 py-2 border-b border-[var(--border-subtle)]">
              <div className="flex items-center gap-3">
                <h3 className="text-xs font-semibold text-[var(--text-primary)]">Edit: {editingConcept.name}</h3>
                <span className="text-[10px] text-[var(--text-muted)]">{editingConcept.creator_name}</span>
              </div>
              <button onClick={() => setEditingConcept(null)} className="btn-ghost btn-icon-xs"><X size={14} /></button>
            </div>

            {/* Horizontal controls layout */}
            <EditDrawerContent
              concept={editingConcept}
              onRegenerate={(id, prompt, eq) => handleRegenerate(id, prompt, eq)}
              onUpdate={() => { fetchAllConcepts(); onUpdate?.(); }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Version dialog */}
      {versionConcept && <VersionDialog projectId={projectId} concept={versionConcept} onClose={() => setVersionConcept(null)} />}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center" onClick={() => setDeleteConfirm(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-[var(--bg-surface)] border border-[var(--border-default)] rounded-2xl p-5 max-w-xs shadow-2xl">
            <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-2">Delete Concept?</h3>
            <p className="text-xs text-[var(--text-muted)] mb-4">Delete "{deleteConfirm.name}"? This cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteConfirm(null)} className="btn-ghost px-3 py-1.5 text-xs">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="px-3 py-1.5 text-xs rounded-lg bg-[var(--color-error)] text-white hover:bg-[rgba(244,63,94,0.8)]">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
